import React, { useEffect, useRef, useState } from "react";
import "./App.css";
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import FemtyzHome from "./pages/FemtyzHome";
import SettingsUI from "./pages/SettingsUI";
import SearchResults from "./pages/SearchResults";
import ProductDetail from "./pages/ProductDetail";
import ProductDetailSingle from "./pages/ProductDetailSingle";
import Subscription from "./pages/Subscription";
import ContactUsV2 from "./pages/ContactUsV2";
import TermsAndConditions from "./pages/TermsAndConditions";
import FAQ from "./pages/FAQ";
import About from "./pages/About";
import PlatformProducts from "./pages/PlatformProducts";
import BaseProducts from "./pages/BaseProducts";
import FeaturesProducts from "./pages/FeaturesProducts";
import TopDealsProducts from "./pages/TopDealsProducts";
import DataPricingTransparency from "./pages/DataPricingTransparency";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import HowItWorks from "./pages/HowItWorks";
import PasteListStep from "./pages/PasteListStep";
import PasteListReview from "./pages/PasteListReview";
import PasteListSummary from "./pages/PasteListSummary";
import AuthOverlay from "./components/auth/AuthOverlay";
import ForgotPasswordOverlay from "./components/auth/ForgotPasswordOverlay";
import MobileMenuDrawer from "./components/MobileMenuDrawer";
import { logout } from "./store/auth/authSlice";
import { ToastProvider } from "./components/ToastProvider";
import { fetchPlans } from "./store/plans/plansSlice";
import { useToast } from "./components/ToastProvider";
import { fetchCurrentUser } from "./store/auth/authSlice";
import { isAuthErrorMessage } from "./api/client";
import { clearHomeQuickCompareMemory } from "./utils/catalogTabState";

function resetHomeTabs() {
  clearHomeQuickCompareMemory();
  window.dispatchEvent(new Event("femtyz:reset-home-tabs"));
}

function ScrollToTop() {
  const { pathname, search, hash } = useLocation();

  useEffect(() => {
    // Scroll after route changes (button clicks / Link navigation)
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, [pathname, search, hash]);

  return null;
}

function AppShell() {
  const [auth, setAuth] = useState({ open: false, mode: "login" });
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const token = useSelector((state) => state.auth.token);
  const authStatus = useSelector((state) => state.auth.status);
  const authError = useSelector((state) => state.auth.error);
  const isLoggedIn = Boolean(token);
  const { showToast } = useToast();
  const prevTokenRef = useRef(token);
  const prevAuthErrorRef = useRef(authError);
  const suppressLogoutToastRef = useRef(false);
  const authExpiredToastShownRef = useRef(false);

  function handleOpenAuth(mode) {
    setAuth({ open: true, mode: mode || "login" });
  }

  useEffect(() => {
    const handler = (e) => {
      const mode = e?.detail?.mode || "login";
      handleOpenAuth(mode);
    };
    window.addEventListener("femtyz:open-auth", handler);
    return () => window.removeEventListener("femtyz:open-auth", handler);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (authExpiredToastShownRef.current) return;
      authExpiredToastShownRef.current = true;
      window.setTimeout(() => {
        authExpiredToastShownRef.current = false;
      }, 5000);

      const message =
        e?.detail?.message || "Your session has expired. Please login again.";
      suppressLogoutToastRef.current = true;
      if (token) {
        dispatch(logout());
      }
      showToast({
        type: "error",
        title: "Session expired",
        message,
        duration: 6000,
      });
    };
    window.addEventListener("femtyz:auth-expired", handler);
    return () => window.removeEventListener("femtyz:auth-expired", handler);
  }, [token, dispatch, showToast]);

  useEffect(() => {
    const prevToken = prevTokenRef.current;
    if (prevToken && !token) {
      if (!suppressLogoutToastRef.current) {
        showToast({ type: "error", title: "Logged out", message: "You have been logged out." });
      }
      suppressLogoutToastRef.current = false;
    }
    prevTokenRef.current = token;
  }, [token, showToast]);

  useEffect(() => {
    if (!token) return;
    dispatch(fetchCurrentUser({ force: true }));
  }, [token, dispatch]);

  useEffect(() => {
    if (authStatus !== "failed") return;
    if (!authError) return;
    if (prevAuthErrorRef.current === authError) return;
    prevAuthErrorRef.current = authError;
    if (isAuthErrorMessage(authError)) return;
    showToast({ type: "error", title: "Auth failed", message: authError });
  }, [authStatus, authError, showToast]);

  return (
    <>
      <Routes>
        <Route
          path="/"
          element={
            <FemtyzHome
              onOpenAuth={handleOpenAuth}
              onOpenMenu={() => setMenuOpen(true)}
            />
          }
        />
        <Route
          path="/settings"
          element={
            <SettingsUI
              onOpenAuth={handleOpenAuth}
              onOpenMenu={() => setMenuOpen(true)}
            />
          }
        />
        <Route
          path="/search"
          element={
            <SearchResults
              onOpenAuth={handleOpenAuth}
              onOpenMenu={() => setMenuOpen(true)}
            />
          }
        />
        <Route
          path="/products"
          element={
            <BaseProducts
              onOpenAuth={handleOpenAuth}
              onOpenMenu={() => setMenuOpen(true)}
            />
          }
        />
        <Route
          path="/product/:id"
          element={
            <ProductDetail
              onOpenAuth={handleOpenAuth}
              onOpenMenu={() => setMenuOpen(true)}
            />
          }
        />
        <Route
          path="/product-store/:platform/:id"
          element={
            <ProductDetailSingle
              onOpenAuth={handleOpenAuth}
              onOpenMenu={() => setMenuOpen(true)}
            />
          }
        />
        <Route
          path="/subscription"
          element={
            <Subscription
              onOpenAuth={handleOpenAuth}
              onOpenMenu={() => setMenuOpen(true)}
            />
          }
        />
        <Route
          path="/billing/success"
          element={
            <Subscription
              onOpenAuth={handleOpenAuth}
              onOpenMenu={() => setMenuOpen(true)}
            />
          }
        />
        <Route
          path="/billing/cancel"
          element={
            <Subscription
              onOpenAuth={handleOpenAuth}
              onOpenMenu={() => setMenuOpen(true)}
            />
          }
        />
        <Route
          path="/about"
          element={
            <About
              onOpenAuth={handleOpenAuth}
              onOpenMenu={() => setMenuOpen(true)}
            />
          }
        />
        <Route
          path="/contact"
          element={
            <ContactUsV2
              onOpenAuth={handleOpenAuth}
              onOpenMenu={() => setMenuOpen(true)}
            />
          }
        />
        <Route
          path="/terms"
          element={
            <TermsAndConditions
              onOpenAuth={handleOpenAuth}
              onOpenMenu={() => setMenuOpen(true)}
            />
          }
        />
        <Route
          path="/faq"
          element={
            <FAQ
              onOpenAuth={handleOpenAuth}
              onOpenMenu={() => setMenuOpen(true)}
            />
          }
        />
        <Route
          path="/platform/:platform"
          element={
            <PlatformProducts
              onOpenAuth={handleOpenAuth}
              onOpenMenu={() => setMenuOpen(true)}
            />
          }
        />
        <Route
          path="/features"
          element={
            <FeaturesProducts
              onOpenAuth={handleOpenAuth}
              onOpenMenu={() => setMenuOpen(true)}
            />
          }
        />
        <Route
          path="/top-deals"
          element={
            <TopDealsProducts
              onOpenAuth={handleOpenAuth}
              onOpenMenu={() => setMenuOpen(true)}
            />
          }
        />
        <Route
          path="/data-pricing-transparency"
          element={
            <DataPricingTransparency
              onOpenAuth={handleOpenAuth}
              onOpenMenu={() => setMenuOpen(true)}
            />
          }
        />
        <Route
          path="/privacy-policy"
          element={
            <PrivacyPolicy
              onOpenAuth={handleOpenAuth}
              onOpenMenu={() => setMenuOpen(true)}
            />
          }
        />
        <Route
          path="/how-it-works"
          element={
            <HowItWorks
              onOpenAuth={handleOpenAuth}
              onOpenMenu={() => setMenuOpen(true)}
            />
          }
        />
        <Route
          path="/paste-list"
          element={
            <PasteListStep
              onOpenAuth={handleOpenAuth}
              onOpenMenu={() => setMenuOpen(true)}
            />
          }
        />
        <Route
          path="/paste-list/review"
          element={
            <PasteListReview
              onOpenAuth={handleOpenAuth}
              onOpenMenu={() => setMenuOpen(true)}
            />
          }
        />
        <Route
          path="/paste-list/summary"
          element={
            <PasteListSummary
              onOpenAuth={handleOpenAuth}
              onOpenMenu={() => setMenuOpen(true)}
            />
          }
        />
      </Routes>

      <AuthOverlay
        open={auth.open}
        initialMode={auth.mode}
        onClose={() => setAuth((s) => ({ ...s, open: false }))}
        onLoginSuccess={() => {
          setAuth((s) => ({ ...s, open: false }));
          dispatch(fetchPlans());
          dispatch(fetchCurrentUser({ force: true }));
        }}
        onOpenForgotPassword={() => {
          setAuth((s) => ({ ...s, open: false }));
          setForgotPasswordOpen(true);
        }}
      />
      <ForgotPasswordOverlay
        open={forgotPasswordOpen}
        onClose={() => setForgotPasswordOpen(false)}
        onOpenLogin={() => {
          setForgotPasswordOpen(false);
          handleOpenAuth("login");
        }}
      />
      <MobileMenuDrawer
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        onOpenAuth={handleOpenAuth}
        isLoggedIn={isLoggedIn}
        onLogout={() => {
          dispatch(logout());
          setMenuOpen(false);
          resetHomeTabs();
          navigate("/");
        }}
        onSelectMenuItem={(key) => {
          if (key === "home") {
            resetHomeTabs();
            navigate("/");
          }
          if (key === "about") navigate("/about");
          if (key === "profile") {
            navigate("/settings");
          }
          if (key === "subscription") navigate("/subscription");
          if (key === "data & pricing transparency") navigate("/data-pricing-transparency");
          if (key === "contact") navigate("/contact");
          if (key === "privacy policy") navigate("/privacy-policy");
          if (key === "terms & conditions") navigate("/terms");
          if (key === "faq") navigate("/faq");
          if (key === "how it works") navigate("/how-it-works");
        }}
      />
    </>
  );
}

function App() {
  return (
    <ToastProvider>
      <Router>
        <ScrollToTop />
        <AppShell />
      </Router>
    </ToastProvider>
  );
}

export default App;
